import * as LocalAuthentication from 'expo-local-authentication';
import { Platform } from 'react-native';

/**
 * Resultado del guardián biométrico.
 *
 * POR QUÉ TRES ESTADOS Y NO UN BOOLEAN
 *
 * Antes `authenticateUser` devolvía `false` tanto cuando el usuario cancelaba
 * como cuando el teléfono NO TIENE CÓMO verificar (sin lector, o sin huella/
 * PIN configurados). Las pantallas trataban los dos casos igual: bloquear.
 * Resultado: en un teléfono sin bloqueo de pantalla —exactamente el emulador
 * limpio con el que Google Play revisa la app— el usuario quedaba encerrado en
 * el KYC y no podía aceptar un viaje ni un pedido. Eso es un rechazo directo.
 *
 * "El usuario falló la verificación" y "este aparato no puede verificar" son
 * cosas distintas y se responden distinto: la primera se bloquea, la segunda
 * se deja pasar dejando rastro en el log.
 *
 * Nota de seguridad: este guardián NO es el que decide la identidad. La
 * identidad la aprueba una persona revisando la cédula y la selfie
 * (`reviewKycSubmission`), en el servidor. Aquí solo se comprueba que quien
 * tiene el teléfono en la mano es su dueño, cuando el teléfono puede
 * comprobarlo. Dejar pasar a quien no tiene bloqueo de pantalla no abre
 * ninguna puerta que el servidor no siga vigilando.
 */
export type ResultadoGuardiaBiometrica =
  /** El sistema operativo confirmó al dueño del teléfono. */
  | { estado: 'AUTORIZADO' }
  /** El teléfono no puede verificar (sin hardware, sin huella ni PIN, web…). Se deja pasar. */
  | { estado: 'SIN_BLOQUEO_DE_PANTALLA'; motivo: string }
  /** El usuario canceló o falló la verificación. Aquí sí se bloquea. */
  | { estado: 'RECHAZADO'; motivo: string };

/**
 * Errores de `expo-local-authentication` que significan "este aparato no puede
 * verificar", no "el usuario se negó". `lockout` entra aquí a propósito: tras
 * varios intentos fallidos el sistema cierra el lector durante minutos y el
 * usuario se queda sin salida, que es justo lo que hay que evitar.
 */
const ERRORES_DE_APARATO = new Set<string>([
  'not_enrolled',
  'not_available',
  'passcode_not_set',
  'no_space',
  'invalid_context',
  'lockout',
]);

export const biometricService = {
  /**
   * ¿Puede este teléfono verificar al dueño?
   *
   * Se mira tanto la biometría (huella/rostro) como el nivel de bloqueo del
   * dispositivo: con `disableDeviceFallback: false`, un PIN o patrón también
   * sirve para autenticar, así que un teléfono con PIN y sin lector SÍ puede
   * verificar. `SecurityLevel.NONE` es el aparato sin ningún bloqueo.
   */
  async isBiometricAvailable(): Promise<boolean> {
    if (Platform.OS === 'web') return false; // La web no expone el bloqueo del teléfono

    try {
      const [hayHardware, estaEnrolado, nivel] = await Promise.all([
        LocalAuthentication.hasHardwareAsync(),
        LocalAuthentication.isEnrolledAsync(),
        LocalAuthentication.getEnrolledLevelAsync(),
      ]);
      return (hayHardware && estaEnrolado) || nivel !== LocalAuthentication.SecurityLevel.NONE;
    } catch (error) {
      // Si el módulo nativo ni siquiera responde, este aparato no puede
      // verificar. No es culpa del usuario.
      console.warn('[biometría] no se pudo consultar el bloqueo del aparato:', error);
      return false;
    }
  },

  /**
   * Invoca el diálogo nativo de Face ID / Huella / PIN y distingue los tres
   * desenlaces. Úsala cuando la pantalla quiera reaccionar distinto a cada uno.
   *
   * @param promptMessage Mensaje que muestra el diálogo del sistema operativo.
   */
  async verificarIdentidadDelDispositivo(
    promptMessage: string = 'Verifica tu identidad para continuar'
  ): Promise<ResultadoGuardiaBiometrica> {
    if (Platform.OS === 'web') {
      return {
        estado: 'SIN_BLOQUEO_DE_PANTALLA',
        motivo: 'El navegador no expone el bloqueo de pantalla del dispositivo.',
      };
    }

    const puedeVerificar = await this.isBiometricAvailable();
    if (!puedeVerificar) {
      return {
        estado: 'SIN_BLOQUEO_DE_PANTALLA',
        motivo: 'Este teléfono no tiene huella, rostro ni PIN configurados.',
      };
    }

    try {
      const resultado = await LocalAuthentication.authenticateAsync({
        promptMessage,
        cancelLabel: 'Cancelar',
        fallbackLabel: 'Usar PIN',
        // false = si la biometría falla, el sistema ofrece el PIN del teléfono.
        disableDeviceFallback: false,
      });

      if (resultado.success) return { estado: 'AUTORIZADO' };

      if (ERRORES_DE_APARATO.has(resultado.error)) {
        return {
          estado: 'SIN_BLOQUEO_DE_PANTALLA',
          motivo: `El sistema no pudo verificar (${resultado.error}).`,
        };
      }

      return { estado: 'RECHAZADO', motivo: resultado.error };
    } catch (error: any) {
      // Excepción del módulo nativo (emulador sin servicios, ROM recortada…).
      // Antes esto devolvía false y bloqueaba al usuario para siempre.
      console.warn('[biometría] el diálogo del sistema falló:', error?.message);
      return {
        estado: 'SIN_BLOQUEO_DE_PANTALLA',
        motivo: 'El diálogo de seguridad del sistema no se pudo abrir.',
      };
    }
  },

  /**
   * Versión booleana para las pantallas que solo necesitan saber si pueden
   * seguir adelante.
   *
   * Devuelve `false` ÚNICAMENTE cuando el usuario canceló o falló. Si el
   * aparato no puede verificar, devuelve `true` y deja constancia en el log:
   * bloquear ahí no protege nada y deja al usuario sin salida.
   */
  async authenticateUser(promptMessage: string = 'Verifica tu identidad para continuar'): Promise<boolean> {
    const resultado = await this.verificarIdentidadDelDispositivo(promptMessage);
    if (resultado.estado === 'SIN_BLOQUEO_DE_PANTALLA') {
      console.warn('[biometría] se continúa sin verificación del aparato:', resultado.motivo);
      return true;
    }
    return resultado.estado === 'AUTORIZADO';
  },
};
